import { t } from "@lingui/core/macro"
import { Trans } from "@lingui/react/macro"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { useMemo, useState } from "react"
import { shelfCodesQueryOptions } from "@/features/bill-of-materials/hooks/useShelfCodes"
import { HowLink } from "@/features/list-of-materials/components/how-it-works/HowLink"
import { ShelfCodeFamilyGrid } from "@/features/list-of-materials/components/shelf-codes/ShelfCodeFamilyGrid"
import { ShelfCodeFilters } from "@/features/list-of-materials/components/shelf-codes/ShelfCodeFilters"
import { ShelfCodeFormPanel } from "@/features/list-of-materials/components/shelf-codes/ShelfCodeFormPanel"
import { ShelfCodeWorkbook } from "@/features/list-of-materials/components/shelf-codes/ShelfCodeWorkbook"
import { Block, EmptyLine } from "@/features/list-of-materials/components/ui/Block"
import { Button } from "@/features/list-of-materials/components/ui/Button"
import { Chip } from "@/features/list-of-materials/components/ui/Chip"
import { PageHead, PageStatus } from "@/features/list-of-materials/components/ui/PageHead"
import {
  EMPTY_SHELF_CODE_FILTER,
  filterShelfCodes,
  groupShelfCodesByFamily,
} from "@/features/list-of-materials/core/shelfCodeFamilies"
import { toShelfCodeSummary } from "@/features/list-of-materials/core/shelfCodeRows"
import { useCanEditLom } from "@/features/list-of-materials/hooks/useCanEditLom"
import {
  lomVersionsQueryOptions,
  useSelectedLomVersion,
} from "@/features/list-of-materials/hooks/useLomVersion"

export const Route = createFileRoute("/(authenticated)/list-of-materials/shelf-codes/")({
  loaderDeps: ({ search }) => ({ version: search.version }),
  loader: async ({ context, deps }) => {
    const { selected } = await context.queryClient.ensureQueryData(
      lomVersionsQueryOptions(deps.version),
    )
    if (selected) void context.queryClient.ensureQueryData(shelfCodesQueryOptions(selected.id))
  },
  component: ShelfCodesPage,
})

function ShelfCodesPage() {
  const { version, versionSearch } = useSelectedLomVersion()
  const { data: rows } = useSuspenseQuery(shelfCodesQueryOptions(version.id))
  const [filter, setFilter] = useState(EMPTY_SHELF_CODE_FILTER)
  const [creating, setCreating] = useState(false)
  const canEdit = useCanEditLom()
  const navigate = useNavigate()

  const codes = useMemo(() => rows.map(toShelfCodeSummary), [rows])
  const shown = useMemo(() => filterShelfCodes(codes, filter), [codes, filter])
  const groups = useMemo(() => groupShelfCodesByFamily(shown), [shown])

  return (
    <>
      <PageHead
        help={<HowLink topic="shelf-codes" />}
        title={<Trans>Shelf codes</Trans>}
        sub={
          <Trans>
            One meter of racking per code, as in the Racking Guidelines. The guide was read in once;
            changes are made here.
          </Trans>
        }
        right={
          <>
            <PageStatus>
              <Chip tone="ok" dot>
                <Trans>Complete</Trans>
              </Chip>
              {t`${codes.length} codes`}
            </PageStatus>
            {canEdit && (
              <Button tone="primary" onClick={() => setCreating(true)}>
                <Trans>New shelf code</Trans>
              </Button>
            )}
          </>
        }
      />

      <ShelfCodeWorkbook configVersionId={version.id} canEdit={canEdit} />

      <ShelfCodeFilters
        filter={filter}
        onChange={setFilter}
        shown={shown.length}
        total={codes.length}
      />

      {groups.length === 0 ? (
        <Block>
          <EmptyLine>
            <Trans>No shelf code matches this search.</Trans>
          </EmptyLine>
        </Block>
      ) : (
        groups.map((group) => (
          <ShelfCodeFamilyGrid key={group.family} group={group} version={versionSearch.version} />
        ))
      )}

      <ShelfCodeFormPanel
        open={creating}
        onOpenChange={setCreating}
        configVersionId={version.id}
        codes={rows}
        onSaved={(shelfCodeId) =>
          void navigate({
            to: "/list-of-materials/shelf-codes/$shelfCodeId",
            params: { shelfCodeId },
            search: versionSearch,
          })
        }
      />
    </>
  )
}
